import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { selectors as userSelectors } from '@elab/core/store/user';
import {
    actions as cartActions,
    selectors as cartSelectors,
    types as cartTypes,
} from '@elab/core/store/cart';
import { actions as globalActions, selectors as globalSelectors } from '@elab/core/store/global';
import { actions as uiActions } from '@elab/core/store/ui';
import { actions as checkoutActions } from '@elab/core/store/checkout';
import { types as authTypes } from '@elab/core/store/auth';
import { selectors as configSelectors } from '@elab/core/store/config';
import { setDeliveryMode } from '@elab/components/connected/DeliveryModes/redux/saga';
import * as storage from '@elab/core/utils/storage';
import { realTimeStockErrors, storageNames } from '@elab/core/utils/constants';
import { createWatcher } from '@elab/core/store/core/saga';
import { selectors as productSelectors } from '@elab/core/store/product';
import analytics from '@elab/core/utils/analytics';

import api from './api';
import actions from './actions';
import selectors from './selectors';
import types from './types';

export function* onLatestStoreProductStockRequest({ payload: { productCode } }) {
    const lastStoreData = yield select(selectors.getLastStoreData);
    if (lastStoreData && lastStoreData.geoPoint) {
        const lastStoreDataPayload = {
            productCode,
            latitude: lastStoreData.geoPoint.latitude,
            longitude: lastStoreData.geoPoint.longitude,
        };

        yield call(onProductStockRequest, { payload: lastStoreDataPayload });

        const stores = yield select(selectors.getProductStores);
        const inStockInLastStore = !!stores.find(
            ({ geoPoint, stockInfo }) =>
                geoPoint.latitude === lastStoreDataPayload.latitude &&
                geoPoint.longitude === lastStoreDataPayload.longitude &&
                stockInfo.stockLevelStatus === 'inStock',
        );

        yield put(actions.setInStockInLastStore(inStockInLastStore));
    }
}

export function* onProductStockRequest({ payload }) {
    if (payload.modalName) {
        yield put(uiActions.openDialog({ name: payload.modalName }));
    }

    yield call(setLastStoreIsUsed, { payload });

    try {
        // in API file I change query to "location" because API needs location
        const response = yield call(api.fetchProductStock, payload);
        const stores = yield call(setLastUsedStoreAsFirstInList, response.stores);
        const failOnEmptyStores = yield select(configSelectors.failRtsOnEmptyStores);
        if (stores.length < 1 && failOnEmptyStores) {
            yield put(
                actions.productStockResponseFailure({
                    errorType: realTimeStockErrors.NO_STORE_FOUND,
                }),
            );
        }
        yield put(
            actions.productStockResponseSuccess({
                ...response,
                stores,
            }),
        );
    } catch (error) {
        let msg = error.message.split(' ');
        if (msg[msg.length - 1] === 'rts-error-message') {
            msg.pop();
            const message = msg.join(' ');
            yield put(actions.setErrorMessage({ errorMessage: message }));
        }
        yield put(
            actions.productStockResponseFailure({ errorType: realTimeStockErrors.RTS_ERROR }),
        );
    }
}

export function* setLastStoreIsUsed({ payload }) {
    const useRtsLastStore = yield select(configSelectors.useRtsLastStore);
    if (useRtsLastStore) {
        yield put(actions.setLastStoreIsUsed(payload.lastStoreIsUsed));
    }
}

export function* onCartProductsStockRequest({ payload }) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? cartSelectors.getCode : cartSelectors.getGuid);
    yield call(setLastStoreIsUsed, { payload });
    const lastUsedStore = yield call(storage.get, storageNames.lastUsedStore);
    const { latitude = 0, longitude = 0 } = lastUsedStore
        ? JSON.parse(lastUsedStore).geoPoint
            ? JSON.parse(lastUsedStore).geoPoint
            : JSON.parse(lastUsedStore)
        : {};
    let requestPayload = payload;
    if (!requestPayload.query) {
        requestPayload = { ...payload, latitude, longitude };
    }

    try {
        const response = yield call(api.getCartProductsStock, userId, cartId, requestPayload);

        // mapping to achieve the same structure as on onProductStockRequest
        const mappedResponse = response.stores.map(storeData => ({
            ...storeData.store,
            entriesWithStock: storeData.entriesWithStock,
        }));
        let errorType = null;
        if (mappedResponse.length === 0) {
            errorType =
                response.pagination.totalResults === 0
                    ? realTimeStockErrors.NO_STORE_FOUND
                    : realTimeStockErrors.NO_STORE_FOUND_PRODUCT;
        }

        const failOnEmptyStores = yield select(configSelectors.failRtsOnEmptyStores);
        if (errorType && failOnEmptyStores) {
            yield put(actions.productStockResponseFailure({ errorType }));
        } else {
            const stores = yield call(setLastUsedStoreAsFirstInList, mappedResponse);
            yield put(actions.productsStockResponseSuccess(stores));
        }
    } catch (e) {
        yield put(
            actions.productStockResponseFailure({ ...e, errorType: realTimeStockErrors.RTS_ERROR }),
        );
    }

    if (payload.openRtsPopup) {
        yield put(uiActions.openDialog({ name: 'rts-pickup' }));
    }
}

export function* onSetDeliveryStore({ payload }) {
    const { storeId } = payload;
    const deliveryModeId = yield select(cartSelectors.getDeliveryModeCode);
    const deliveryModeDTO = { deliveryModeId, storeId };
    const makeChangeDeliveryAddress = yield select(configSelectors.makeChangeDeliveryAddress);
    yield call(setDeliveryMode, deliveryModeDTO);
    yield call(setLastStore, storeId, true);
    yield put(uiActions.closeDialog());
    yield put(cartActions.loadCCExpressDeliveryDate({ code: deliveryModeId }));

    if (makeChangeDeliveryAddress) {
        yield put(checkoutActions.setChangeDeliveryAddress({ status: false }));
    }
}

export function* setLastStore(storeId, isCart) {
    const useRtsLastStore = yield select(configSelectors.useRtsLastStore);
    if (!useRtsLastStore) return;

    const stores = yield select(isCart ? selectors.getCartStores : selectors.getProductStores);
    const store = stores.find(({ name }) => name === storeId);
    if (!store) return;

    yield put(
        actions.setLastStoreData({
            displayName: store.displayName || store.name,
            geoPoint: store.geoPoint,
        }),
    );
}

export function* onSetInCart({ payload }) {
    const { storeId, productQuantity = 1, checkRtsAvailability } = payload;
    const code = yield select(selectors.getRtsProductCode);
    const baseStore = yield select(globalSelectors.getBaseStore);
    const deliveryModeDTO = {
        deliveryModeId: `${baseStore}-pickup-express`,
        storeId,
        fromPdp: payload.fromPdp,
    };
    const makeChangeDeliveryAddress = yield select(configSelectors.makeChangeDeliveryAddress);
    const setDeliveryModeAfterSetCartDataAction = yield select(
        configSelectors.setDeliveryModeAfterSetCartDataAction,
    );

    const addRTSAnalytics = yield select(configSelectors.addRTSAnalytics);
    if (addRTSAnalytics) {
        const productName = yield select(selectors.getRtsProductRangeName);
        const rtsProductStores = yield select(selectors.getProductStores);
        const storePosition =
            rtsProductStores
                .map(function(e) {
                    return e.name;
                })
                .indexOf(storeId) + 1;
        yield call(analytics.handleAddToCartFromRts, code, productName, storeId, storePosition);
    }

    yield call(setDeliveryMode, deliveryModeDTO);
    yield put(uiActions.closeDialog());
    yield put(
        cartActions.addOrUpdateCart({
            code,
            quantity: productQuantity,
            storeId,
            showAsPopup: payload.showAsPopup,
            analytics: {
                category: 'undefined/undefined/undefined/undefined',
                label: 'product detail page',
            },
        }),
    );

    yield call(setLastStore, storeId);

    yield take(cartTypes.SET_CART_DATA);
    if (setDeliveryModeAfterSetCartDataAction) {
        yield call(setDeliveryMode, deliveryModeDTO);
    }
    if (checkRtsAvailability) {
        yield put(actions.productStockRequestLatestStore({ productCode: code }));
    }
    yield put(uiActions.closeDialog());

    if (makeChangeDeliveryAddress) {
        yield put(checkoutActions.setChangeDeliveryAddress({ status: false }));
    }
}

export function* getStoreFromStorage() {
    const useRtsLastStore = yield select(configSelectors.useRtsLastStore);
    if (!useRtsLastStore) return;

    const lastUsedStore = yield call(storage.get, storageNames.lastUsedStore);
    if (lastUsedStore) {
        yield put(actions.setLastStoreData(JSON.parse(lastUsedStore)));
    }
}

export function* setStoreToStorage({ payload }) {
    yield call(storage.set, storageNames.lastUsedStore, JSON.stringify(payload));
}

export function* onAutoLoadProductStock() {
    const lastUsedStore = yield select(selectors.getLastStoreData);
    if (!lastUsedStore) return;
    yield put(
        actions.productsStockRequest({
            ...lastUsedStore.geoPoint,
            lastStoreIsUsed: true,
        }),
    );
}

function* getDeliveryInformation() {
    const guid = yield select(cartSelectors.getGuid);
    const code = yield select(cartSelectors.getCode);
    const userId = yield select(userSelectors.getUserId);
    const deliveryMode = yield call(api.loadDeliveryModes, userId, userId ? code : guid);
    const pickupExpressMode = deliveryMode.deliveryModes.find(item =>
        item.code.includes('express'),
    );
    yield put(actions.setDeliveryInformation(pickupExpressMode));
}

export function* onShowProductStock({ payload = {} }) {
    yield call(getDeliveryInformation);
    if (!payload.forceClick) {
        yield put(uiActions.openDialog({ name: payload.name }));
    }

    if (payload.sendAnalytics) {
        yield call(
            analytics.handleCheckStockStorageEvent,
            payload.productName,
            payload.productCode,
        );
    }

    const lastUsedStore = yield select(selectors.getLastStoreData);
    if (!lastUsedStore) return;

    const productCodeFromStore = yield select(productSelectors.getProductCode, payload.storeName);

    const productCode = payload.usePayloadCode
        ? payload.productCode
        : productCodeFromStore || payload.productCode;

    yield put(
        actions.productStockRequest({
            productCode,
            ...lastUsedStore.geoPoint,
            lastStoreIsUsed: true,
        }),
    );
}

export function* onShowCartStockFromHome({ payload }) {
    const getPdpUrl = yield select(selectors.getPdpUrl);
    const getQuantity = yield select(selectors.getPdtQuantity);
    yield put(uiActions.navigateToPDP({ forceClick: true, productQuantity: getQuantity }));
    yield put(globalActions.redirect(getPdpUrl.replace('/', '')));
    payload.forceClick = true;
    yield put(actions.showProductStock({ ...payload }));
}

export function* onShowCartStock({ payload }) {
    yield put(uiActions.openDialog({ name: payload.name }));
    const lastUsedStore = yield select(selectors.getLastStoreData);
    if (!lastUsedStore) return;

    yield put(
        actions.productsStockRequest({
            ...lastUsedStore.geoPoint,
            lastStoreIsUsed: true,
        }),
    );
}

export function* setLastUsedStoreAsFirstInList(stores) {
    const useRtsLastStore = yield select(configSelectors.useRtsLastStore);
    const lastUsedStore = yield select(selectors.getLastStoreData);
    if (!useRtsLastStore || !lastUsedStore || !stores.length) {
        return stores;
    }

    const lastUsedStoreData = stores.filter(({ name }) => name === lastUsedStore.displayName);
    const otherStores = stores.filter(({ name }) => name !== lastUsedStore.displayName);
    const storesWithLastUsedAsFirst = [].concat(lastUsedStoreData, ...otherStores);

    return storesWithLastUsedAsFirst;
}

export default () => [
    takeEvery(types.LATEST_STORE_PRODUCT_STOCK_REQUEST, onLatestStoreProductStockRequest),
    takeEvery(types.PRODUCT_STOCK_REQUEST, onProductStockRequest),
    takeEvery(types.PRODUCTS_STOCK_REQUEST, onCartProductsStockRequest),
    takeEvery(types.SET_DELIVERY_STORE, onSetDeliveryStore),
    takeEvery(types.SET_IN_CART, onSetInCart),
    takeEvery(types.INITIALIZE, getStoreFromStorage),
    takeEvery(types.SET_LAST_STORE_DATA, setStoreToStorage),
    takeEvery(types.SHOW_PRODUCT_STOCK, onShowProductStock),
    takeEvery(types.AUTO_LOAD_PRODUCT_STOCK, onAutoLoadProductStock),
    takeEvery(types.SHOW_CART_STOCK, onShowCartStock),
    takeLatest(types.NAVIGATE_AND_SHOW_CART_STOCK, onShowCartStockFromHome),
    createWatcher([authTypes.AFTER_LOGOUT, authTypes.LOGIN_DONE], actions.initialize()),
];
